import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';

/* ─── Design tokens ─────────────────────────────────────────────────── */
const C = {
  navy:        '#0e225d',
  navyMid:     '#1a3370',
  navyDark:    '#0a1a3d',
  gold:        '#fbb21f',
  goldLight:   '#ffc93c',
  goldDim:     'rgba(251,178,31,0.18)',
  goldBorder:  'rgba(251,178,31,0.35)',
  white:       '#ffffff',
  border:      '#e5e7eb',
  bg:          '#f5f7fa',
  muted:       '#9ca3af',
  sub:         '#6b7280',
  body:        '#1f2937',
  green:       '#16a34a',
  greenDim:    'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red:         '#dc2626',
  redDim:      'rgba(220,38,38,0.08)',
  redBorder:   'rgba(220,38,38,0.25)',
};

/* ─── Types ─────────────────────────────────────────────────────────── */
interface QuestionOption {
  option_text: string;
  is_correct:  boolean;
}

interface Question {
  question_text: string;
  marks:         number;
  options:       QuestionOption[];
}

interface ClassTest {
  class_test_id:   string;
  institute_id:    string;
  institute_name:  string | null;
  branch_id:       string | null;
  branch_name:     string | null;
  batch_id:        string | null;
  batch_name:      string | null;
  title:           string;
  subject:         string;
  schedule_date:   string | null;
  schedule_time:   string | null;
  status:          'scheduled' | 'conducted' | 'cancelled';
  conducted_date:  string | null;
  questions:       Question[];
  created_at:      string | null;
  created_by:      string | null;
  created_by_name: string | null;
}

/* ─── Sub-components ────────────────────────────────────────────────── */
const SectionCard: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div style={{ background: C.white, borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
      <div style={{ width: '40px', height: '40px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={`pi ${icon}`} style={{ color: C.gold, fontSize: '1.1rem' }} />
      </div>
      <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: C.navy }}>{title}</h2>
    </div>
    {children}
  </div>
);

const InfoField: React.FC<{ label: string; value: string | null; valueColor?: string }> = ({ label, value, valueColor }) => (
  <div>
    <p style={{ margin: '0 0 5px 0', fontSize: '0.68rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</p>
    <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: valueColor || C.body }}>{value || '—'}</p>
  </div>
);

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

const STATUS_MAP = {
  scheduled: { bg: C.goldDim,  text: '#a06000',  border: C.goldBorder,  icon: 'pi-calendar-clock', label: 'Scheduled' },
  conducted: { bg: C.greenDim, text: '#166534',  border: C.greenBorder, icon: 'pi-check-circle',   label: 'Conducted' },
  cancelled: { bg: C.redDim,   text: C.red,      border: C.redBorder,   icon: 'pi-times-circle',   label: 'Cancelled' },
};

/* ─── Main Component ─────────────────────────────────────────────────── */
const ViewClassTest: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const [test,    setTest]    = useState<ClassTest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [activeQ, setActiveQ] = useState(0);

  useEffect(() => {
    if (id) fetchTest();
  }, [id]);

  const fetchTest = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.get(`/v1/class-tests/${id}`);
      if (res.data.success) {
        setTest(res.data.data);
      } else {
        setError('Failed to load class test');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load class test');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!test || !window.confirm(`Delete "${test.title}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/v1/class-tests/${test.class_test_id}`);
      toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Class test deleted', life: 2500 });
      setTimeout(() => navigate(-1), 1000);
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Delete failed', life: 3000 });
    }
  };

  /* ── Loading ──────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '16px', color: C.navy, fontSize: '0.95rem', fontWeight: '500' }}>Loading class test…</p>
        </div>
      </div>
    );
  }

  /* ── Error ────────────────────────────────────────────────────────── */
  if (error || !test) {
    return (
      <div style={{ padding: '24px' }}>
        <Message severity="error" text={error || 'Class test not found'} style={{ marginBottom: '20px', fontSize: '0.9rem' }} />
        <button
          onClick={() => navigate(-1)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: C.navy, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}
        >
          <i className="pi pi-arrow-left" /> Go Back
        </button>
      </div>
    );
  }

  const status      = STATUS_MAP[test.status] ?? STATUS_MAP.scheduled;
  const totalMarks  = test.questions.reduce((s, q) => s + q.marks, 0);
  const answeredQs  = test.questions.filter(q => q.options.some(o => o.is_correct)).length;

  return (
    <>
      <style>{`
        .vct-qpill:hover { opacity: 0.8; cursor: pointer; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .vct-anim { animation: fadeIn 0.2s ease-out; }
      `}</style>

      <Toast ref={toast} />

      <div style={{ margin: '0 auto', minHeight: '100vh' }}>

        {/* ── Hero Header ─────────────────────────────────────────────── */}
        <div style={{
          background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
          borderRadius: '20px', padding: '32px', marginBottom: '24px',
          color: C.white, position: 'relative', overflow: 'hidden',
          boxShadow: '0 4px 18px rgba(14,34,93,0.25)',
        }}>
          {/* Decorative orbs */}
          <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: C.goldDim, borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-40px', left: '30%', width: '140px', height: '140px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Back button */}
            <button
              onClick={() => navigate(-1)}
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: C.white, padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '20px', transition: 'all 0.2s ease' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            >
              <i className="pi pi-arrow-left" /> Back
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '24px' }}>

              {/* Left — title + chips */}
              <div>
                {/* Eyebrow badge */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', marginBottom: '12px', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: C.gold }}>
                  <i className="pi pi-book" style={{ fontSize: '0.7rem' }} />
                  Class Test
                </div>

                <h1 style={{ margin: '0 0 6px 0', fontSize: '1.75rem', fontWeight: '700', color: C.white, letterSpacing: '-0.5px' }}>
                  {test.title}
                </h1>
                <p style={{ margin: '0 0 16px 0', color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
                  {test.branch_name ?? '—'} {test.batch_name ? `· ${test.batch_name}` : ''}
                </p>

                {/* Info chips */}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {[
                    { icon: 'pi-list',       label: `${test.questions.length} Questions` },
                    { icon: 'pi-star',       label: `${totalMarks} Marks` },
                    ...(test.schedule_date ? [{ icon: 'pi-calendar', label: test.schedule_date }] : []),
                    ...(test.schedule_time ? [{ icon: 'pi-clock',    label: test.schedule_time }] : []),
                    ...(test.created_by_name ? [{ icon: 'pi-user',   label: `By ${test.created_by_name}` }] : []),
                  ].map(chip => (
                    <div key={chip.label} style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', padding: '8px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <i className={`pi ${chip.icon}`} style={{ fontSize: '0.8rem', color: C.gold }} />
                      <span style={{ fontSize: '0.82rem', fontWeight: '600', color: C.white }}>{chip.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — status + actions */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                {/* Status badge */}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.5px', background: status.bg, color: status.text, border: `1px solid ${status.border}` }}>
                  <i className={`pi ${status.icon}`} style={{ fontSize: '0.72rem' }} />
                  {status.label.toUpperCase()}
                </span>

                {/* Edit button */}
                <button
                  onClick={() => navigate(`/class-tests/${test.class_test_id}/edit`)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px', background: C.gold, border: 'none', borderRadius: '12px', color: C.navy, fontSize: '0.88rem', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(251,178,31,0.4)', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', letterSpacing: '0.3px' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(251,178,31,0.5)'; e.currentTarget.style.background = C.goldLight; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(251,178,31,0.4)'; e.currentTarget.style.background = C.gold; }}
                >
                  <i className="pi pi-pencil" style={{ fontSize: '0.9rem' }} />
                  EDIT TEST
                </button>

                {test.created_at && (
                  <p style={{ margin: 0, fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)' }}>
                    Created {test.created_at.split(' ')[0]}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Body grid ───────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px', marginBottom: '24px' }}>

          {/* ── Left: questions ───────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Test Details */}
            <SectionCard title="Test Details" icon="pi-file-edit">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
                <InfoField label="Title"          value={test.title} />
                <InfoField label="Subject"        value={test.subject} />
                <InfoField label="Batch"          value={test.batch_name} />
                <InfoField label="Branch"         value={test.branch_name} />
                <InfoField label="Schedule Date"  value={test.schedule_date} />
                <InfoField label="Schedule Time"  value={test.schedule_time} />
                <InfoField label="Status"         value={status.label} valueColor={status.text} />
                {test.conducted_date && (
                  <InfoField label="Conducted On" value={test.conducted_date.split(' ')[0]} />
                )}
                <InfoField label="Created By"     value={test.created_by_name} />
              </div>
            </SectionCard>

            {/* Questions */}
            <SectionCard title={`Questions (${test.questions.length})`} icon="pi-list">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {test.questions.map((q, qi) => (
                  <div
                    key={qi}
                    className="vct-anim"
                    onClick={() => setActiveQ(qi)}
                    style={{
                      border: `1.5px solid ${activeQ === qi ? C.navy : C.border}`,
                      borderRadius: '12px', overflow: 'hidden',
                      boxShadow: activeQ === qi ? '0 2px 12px rgba(14,34,93,0.1)' : 'none',
                      transition: 'all 0.15s', cursor: 'default',
                    }}
                  >
                    {/* Question header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: activeQ === qi ? 'rgba(14,34,93,0.04)' : C.bg, borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ background: C.navy, color: C.white, fontSize: '0.7rem', fontWeight: '700', padding: '3px 10px', borderRadius: '20px' }}>Q {qi + 1}</span>
                        <span style={{ background: C.goldDim, color: '#a06000', border: `1px solid ${C.goldBorder}`, fontSize: '0.7rem', fontWeight: '600', padding: '3px 10px', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <i className="pi pi-star" style={{ fontSize: '0.62rem' }} />{q.marks} {q.marks === 1 ? 'mark' : 'marks'}
                        </span>
                      </div>
                    </div>

                    {/* Question body */}
                    <div style={{ padding: '14px 16px' }}>
                      <p style={{ margin: '0 0 14px 0', fontSize: '0.88rem', fontWeight: '600', color: C.body, lineHeight: 1.55 }}>
                        {q.question_text}
                      </p>

                      {/* Options */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                        {q.options.map((opt, oi) => (
                          <div
                            key={oi}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '9px 12px', borderRadius: '10px',
                              background: opt.is_correct ? C.goldDim : C.bg,
                              border: `1.5px solid ${opt.is_correct ? C.goldBorder : C.border}`,
                              transition: 'all 0.12s',
                            }}
                          >
                            {/* Letter badge */}
                            <div style={{
                              width: '26px', height: '26px', flexShrink: 0, borderRadius: '7px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.72rem', fontWeight: '700',
                              background: opt.is_correct ? C.gold : C.white,
                              border: opt.is_correct ? `1.5px solid ${C.gold}` : `1.5px solid ${C.border}`,
                              color: opt.is_correct ? C.navy : C.muted,
                            }}>
                              {OPTION_LETTERS[oi]}
                            </div>

                            <span style={{ flex: 1, fontSize: '0.85rem', color: opt.is_correct ? C.body : C.sub, fontWeight: opt.is_correct ? '600' : '400' }}>
                              {opt.option_text}
                            </span>

                            {opt.is_correct && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', fontWeight: '700', color: C.green, background: C.greenDim, border: `1px solid ${C.greenBorder}`, borderRadius: '20px', padding: '2px 9px', flexShrink: 0 }}>
                                <i className="pi pi-check" style={{ fontSize: '0.62rem' }} /> Correct
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>

          {/* ── Right sidebar ─────────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* At a Glance */}
            <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="pi pi-chart-bar" style={{ color: C.gold, fontSize: '1rem' }} />
                </div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: C.navy }}>At a Glance</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { icon: 'pi-list',         label: 'Total Questions', value: test.questions.length },
                  { icon: 'pi-star',         label: 'Total Marks',     value: totalMarks            },
                  { icon: 'pi-check-circle', label: 'Ready',           value: answeredQs            },
                ].map(stat => (
                  <div key={stat.label} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: C.bg, padding: '12px', borderRadius: '10px', border: `1px solid ${C.border}` }}>
                    <div style={{ width: '38px', height: '38px', flexShrink: 0, background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className={`pi ${stat.icon}`} style={{ color: C.gold, fontSize: '0.9rem' }} />
                    </div>
                    <div>
                      <p style={{ margin: '0 0 2px 0', fontSize: '0.68rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{stat.label}</p>
                      <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: '800', color: C.navy, lineHeight: 1.1 }}>{stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Question palette */}
            <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="pi pi-th-large" style={{ color: C.gold, fontSize: '1rem' }} />
                </div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: C.navy }}>Questions</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                {test.questions.map((q, qi) => {
                  const isActive  = qi === activeQ;
                  const hasAnswer = q.options.some(o => o.is_correct);
                  let bg = C.white, color = C.sub, border = C.border;
                  if (isActive)   { bg = C.navy;     color = C.white;   border = C.navy; }
                  else if (hasAnswer) { bg = C.goldDim; color = '#a06000'; border = C.goldBorder; }
                  return (
                    <button
                      key={qi}
                      className="vct-qpill"
                      onClick={() => {
                        setActiveQ(qi);
                        document.querySelectorAll('.vct-anim')[qi]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      style={{ width: '100%', aspectRatio: '1', borderRadius: '8px', border: `1.5px solid ${border}`, background: bg, color, fontSize: '11px', fontWeight: '700', transition: 'all 0.12s', cursor: 'pointer' }}
                    >
                      {qi + 1}
                    </button>
                  );
                })}
              </div>
              {/* Legend */}
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {[
                  { bg: C.navy,    border: C.navy,       label: 'Active'   },
                  { bg: C.goldDim, border: C.goldBorder, label: 'Has answer' },
                  { bg: C.white,   border: C.border,     label: 'No answer' },
                ].map(({ bg, border, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: C.sub }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '3px', background: bg, border: `1.5px solid ${border}`, flexShrink: 0 }} />
                    {label}
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="pi pi-bolt" style={{ color: C.gold, fontSize: '1rem' }} />
                </div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: C.navy }}>Quick Actions</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { icon: 'pi-pencil', label: 'Edit Test',    onClick: () => navigate(`/class-tests/${test.class_test_id}/edit`) },
                  { icon: 'pi-copy',   label: 'Duplicate',    onClick: () => {} /* wire as needed */ },
                  { icon: 'pi-trash',  label: 'Delete Test',  onClick: handleDelete, danger: true },
                ].map(action => (
                  <button
                    key={action.label}
                    onClick={action.onClick}
                    style={{ width: '100%', padding: '11px 14px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.2s ease', textAlign: 'left' }}
                    onMouseEnter={e => {
                      const bg    = action.danger ? C.redDim  : C.navy;
                      const bord  = action.danger ? C.redBorder : C.navy;
                      const lbl   = action.danger ? C.red     : C.white;
                      const chev  = action.danger ? C.red     : 'rgba(255,255,255,0.4)';
                      e.currentTarget.style.background   = bg;
                      e.currentTarget.style.borderColor  = bord;
                      (e.currentTarget.querySelector('.qa-icon')   as HTMLElement).style.color = lbl;
                      (e.currentTarget.querySelector('.qa-label')  as HTMLElement).style.color = lbl;
                      (e.currentTarget.querySelector('.qa-chevron') as HTMLElement).style.color = chev;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background  = C.bg;
                      e.currentTarget.style.borderColor = C.border;
                      const def = action.danger ? C.red : C.navy;
                      (e.currentTarget.querySelector('.qa-icon')   as HTMLElement).style.color = def;
                      (e.currentTarget.querySelector('.qa-label')  as HTMLElement).style.color = C.body;
                      (e.currentTarget.querySelector('.qa-chevron') as HTMLElement).style.color = C.muted;
                    }}
                  >
                    <i className={`pi ${action.icon} qa-icon`} style={{ fontSize: '0.9rem', color: action.danger ? C.red : C.navy, transition: 'color 0.2s' }} />
                    <span className="qa-label" style={{ fontSize: '0.85rem', fontWeight: '600', color: C.body, transition: 'color 0.2s' }}>{action.label}</span>
                    <i className="pi pi-chevron-right qa-chevron" style={{ marginLeft: 'auto', fontSize: '0.7rem', color: C.muted, transition: 'color 0.2s' }} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewClassTest;